/**
 * inference/types.js — canonical type definitions for the §6.2 contract.
 *
 * Source of truth for the inference contract. MockInferenceClient and
 * HttpInferenceClient produce values that conform; tool/ + harness/ consume.
 *
 * @typedef {'PII' | 'Sensitive' | 'Inferred'} Category
 *
 * @typedef {Object} InferenceSpan
 * @property {number} start
 * @property {number} end
 *
 * @typedef {Object} Inference
 * @property {string} id
 * @property {string} trait
 * @property {Category} category
 * @property {number} severity     0..1
 * @property {number} confidence   0..1
 * @property {string} reasoning    plain model explanation
 * @property {InferenceSpan[]} spans
 *
 * @typedef {'Location'|'Age'|'Sex'|'Occupation'|'PlaceOfBirth'|'MaritalStatus'|'Income'|'EducationLevel'} AxisName
 *
 * @typedef {Object} RiskAxis
 * @property {AxisName} axis
 * @property {number} meValue      0..1 risk (1 = highest risk / most exposed)
 * @property {number} themValue    0..1 risk (1 = highest risk / most exposed)
 *
 * @typedef {Object} DraftSpan
 * @property {number} start
 * @property {number} end
 * @property {number} severity
 * @property {number} confidence
 * @property {string} inferredTrait
 * @property {string} replacement
 * @property {string} [justification]
 *
 * @typedef {Object} AxisEvidenceItem
 * @property {string} msgId
 * @property {string} snippet
 * @property {string} trait
 * @property {number} confidence
 *
 * @typedef {Object} AxisEvidence
 * @property {AxisName} axis
 * @property {AxisEvidenceItem[]} contributing
 * @property {string} explanation
 *
 * @typedef {Object} Message
 * @property {'me' | 'them'} role
 * @property {string} text
 *
 * @typedef {Object} InferenceResponse
 * @property {Inference[]} messageInferences
 * @property {RiskAxis[]} meterUpdate
 * @property {Object} [mirrorAggregate]
 *
 * @typedef {Object} DraftRequest
 * @property {string} conversationId
 * @property {string} draft
 *
 * @typedef {Object} DraftResponse
 * @property {DraftSpan[]} spans
 *
 * @typedef {Object} ChatMessage
 * @property {string} text
 * @property {number} [thinkingMs]   optional pacing override: pause before "typing" starts
 * @property {number} [typingMs]     optional pacing override: how long the "typing…" phase lasts
 *
 * @typedef {Object} ChatResponse
 * @property {ChatMessage[]} messages   a burst of 1+ consecutive reply bubbles
 */

// Bumped 1.0.0 → 2.0.0: RiskAxis (was AnonymityAxis) flips meValue/themValue
// semantics — the value is now risk (1 = highest risk) rather than anonymity
// (1 = fully anonymous). Breaking for any consumer that read the old meaning.
// 3.0.0 splits the former combined send-time inference response into focused
// background endpoints. Message findings, mirror, and meter/evidence now have
// independent request and response contracts.
// 3.1.0 (additive, backward-compatible): the chat reply grows from a single
// { text } into { messages: ChatMessage[] } so a reply can arrive as a natural
// burst of consecutive bubbles. Legacy { text } still normalizes to one message
// (see normalizeChatResponse in client.js), so no consumer breaks.
// 4.0.0 (breaking): draft scoring sends only its conversation ID and current text.
export const CONTRACT_VERSION = '4.0.0';

// Frozen so a stray .push('Foo') anywhere in the codebase can't silently
// expand what the validators accept.
/** @type {readonly Category[]} */
export const CATEGORIES = Object.freeze(['PII', 'Sensitive', 'Inferred']);

/** @type {readonly AxisName[]} */
export const RISK_AXES = Object.freeze([
  'Location',
  'Age',
  'Sex',
  'Occupation',
  'PlaceOfBirth',
  'MaritalStatus',
  'Income',
  'EducationLevel',
]);

/**
 * Tagged error for §6.5 scope-safeguard violations. Lets callers
 * distinguish a privacy-contract trip from a generic Error / network
 * failure when handling rejections.
 */
export class ScopeViolationError extends Error {
  constructor(message) {
    super(message);
    this.name = 'ScopeViolationError';
  }
}

const isUnit = (n) => typeof n === 'number' && Number.isFinite(n) && n >= 0 && n <= 1;
const isNonEmptyString = (s) => typeof s === 'string' && s.length > 0;

function fail(label, msg) {
  throw new TypeError(`[contract] ${label}: ${msg}`);
}

/** @param {unknown} x */
export function assertInference(x) {
  if (x === null || typeof x !== 'object') fail('Inference', 'expected object');
  const o = /** @type {any} */ (x);
  if (!isNonEmptyString(o.id)) fail('Inference', 'id must be non-empty string');
  if (!isNonEmptyString(o.trait)) fail('Inference', 'trait must be non-empty string');
  if (!CATEGORIES.includes(o.category))
    fail('Inference', `category must be one of ${CATEGORIES.join(', ')}`);
  if (!isUnit(o.severity)) fail('Inference', 'severity must be in [0, 1]');
  if (!isUnit(o.confidence)) fail('Inference', 'confidence must be in [0, 1]');
  if (typeof o.reasoning !== 'string') fail('Inference', 'reasoning must be string');
  if (!Array.isArray(o.spans)) fail('Inference', 'spans must be array');
  for (const s of o.spans) {
    if (
      s === null ||
      typeof s !== 'object' ||
      typeof s.start !== 'number' ||
      typeof s.end !== 'number'
    ) {
      fail('Inference', 'span must have numeric start and end');
    }
  }
}

/** @param {unknown} x */
export function assertAxis(x) {
  if (x === null || typeof x !== 'object') fail('RiskAxis', 'expected object');
  const o = /** @type {any} */ (x);
  if (!RISK_AXES.includes(o.axis)) fail('RiskAxis', `axis must be one of ${RISK_AXES.join(', ')}`);
  if (!isUnit(o.meValue)) fail('RiskAxis', 'meValue must be in [0, 1]');
  if (!isUnit(o.themValue)) fail('RiskAxis', 'themValue must be in [0, 1]');
}

/** @param {unknown} x */
export function assertDraftSpan(x) {
  if (x === null || typeof x !== 'object') fail('DraftSpan', 'expected object');
  const o = /** @type {any} */ (x);
  if (typeof o.start !== 'number' || typeof o.end !== 'number')
    fail('DraftSpan', 'start/end must be numbers');
  if (o.end <= o.start) fail('DraftSpan', 'end must be greater than start');
  if (!isUnit(o.severity)) fail('DraftSpan', 'severity must be in [0, 1]');
  if (!isUnit(o.confidence)) fail('DraftSpan', 'confidence must be in [0, 1]');
  if (!isNonEmptyString(o.inferredTrait)) fail('DraftSpan', 'inferredTrait required');
  if (typeof o.replacement !== 'string') fail('DraftSpan', 'replacement must be string');
}

/** @param {unknown} x */
export function assertDraftRequest(x) {
  if (x === null || typeof x !== 'object' || Array.isArray(x)) {
    fail('DraftRequest', 'expected object');
  }
  const o = /** @type {any} */ (x);
  if (!isNonEmptyString(o.conversationId)) {
    fail('DraftRequest', 'conversationId must be a non-empty string');
  }
  if (typeof o.draft !== 'string') fail('DraftRequest', 'draft must be string');
  if (Object.keys(o).some((key) => key !== 'conversationId' && key !== 'draft')) {
    fail('DraftRequest', 'unexpected property');
  }
}

/** @param {unknown} x */
export function assertMessage(x) {
  if (x === null || typeof x !== 'object') fail('Message', 'expected object');
  const o = /** @type {any} */ (x);
  if (o.role !== 'me' && o.role !== 'them') fail('Message', "role must be 'me' or 'them'");
  if (typeof o.text !== 'string') fail('Message', 'text must be string');
}

/** @param {unknown} x */
export function assertTranscript(x) {
  if (!Array.isArray(x)) fail('Transcript', 'messages must be an array');
  for (const message of x) {
    assertMessage(message);
    if (!isNonEmptyString(message?.id))
      fail('Transcript', 'each message id must be a non-empty string');
  }
}

/** @param {unknown} x */
export function assertInferenceResponse(x) {
  if (x === null || typeof x !== 'object') fail('InferenceResponse', 'expected object');
  const o = /** @type {any} */ (x);
  if (!Array.isArray(o.messageInferences))
    fail('InferenceResponse', 'messageInferences must be array');
  if (!Array.isArray(o.meterUpdate)) fail('InferenceResponse', 'meterUpdate must be array');
  o.messageInferences.forEach(assertInference);
  o.meterUpdate.forEach(assertAxis);
}

/** @param {unknown} x */
export function assertAxisEvidence(x) {
  if (x === null || typeof x !== 'object') fail('AxisEvidence', 'expected object');
  const o = /** @type {any} */ (x);
  if (!RISK_AXES.includes(o.axis))
    fail('AxisEvidence', `axis must be one of ${RISK_AXES.join(', ')}`);
  if (typeof o.explanation !== 'string') fail('AxisEvidence', 'explanation must be string');
  if (!Array.isArray(o.contributing)) fail('AxisEvidence', 'contributing must be array');
  for (const item of o.contributing) {
    if (
      item === null ||
      typeof item !== 'object' ||
      !isNonEmptyString(item.msgId) ||
      !isNonEmptyString(item.snippet) ||
      !isNonEmptyString(item.trait) ||
      !isUnit(item.confidence)
    ) {
      fail('AxisEvidence', 'contributing item shape invalid');
    }
  }
}

/** @param {unknown} x */
export function assertMirrorAggregate(x) {
  if (x === null || typeof x !== 'object') fail('MirrorAggregate', 'expected object');
  const o = /** @type {any} */ (x);
  if (typeof o.sessionLength !== 'string' || !Array.isArray(o.categories)) {
    fail('MirrorAggregate', 'expected sessionLength and categories');
  }
  for (const category of o.categories) {
    if (
      !category ||
      typeof category.key !== 'string' ||
      !isUnit(category.confidence) ||
      typeof category.summary !== 'string' ||
      !Array.isArray(category.evidence)
    ) {
      fail('MirrorAggregate', 'category shape invalid');
    }
    for (const evidence of category.evidence) {
      if (!evidence || typeof evidence.snippet !== 'string' || !isNonEmptyString(evidence.msgId)) {
        fail('MirrorAggregate', 'evidence shape invalid');
      }
    }
  }
}

/** @param {unknown} x */
export function assertMeterResponse(x) {
  if (x === null || typeof x !== 'object') fail('MeterResponse', 'expected object');
  const o = /** @type {any} */ (x);
  if (!Array.isArray(o.meterUpdate)) fail('MeterResponse', 'meterUpdate must be an array');
  o.meterUpdate.forEach(assertAxis);
  if (o.meterUpdate.length !== RISK_AXES.length)
    fail('MeterResponse', 'meterUpdate must include every axis');
  if (
    o.axisEvidence === null ||
    typeof o.axisEvidence !== 'object' ||
    Array.isArray(o.axisEvidence)
  ) {
    fail('MeterResponse', 'axisEvidence must be an object');
  }
  for (const axis of RISK_AXES) assertAxisEvidence(o.axisEvidence[axis]);
}

/**
 * @param {unknown} x
 *
 * Validates the §6.3 chat reply in its 3.1.0 shape: { messages: ChatMessage[] }
 * with at least one message. Callers normalize legacy { text } into this shape
 * (normalizeChatResponse) before asserting, so this only accepts the array form.
 * thinkingMs/typingMs are optional non-negative pacing overrides.
 */
export function assertChatResponse(x) {
  if (x === null || typeof x !== 'object') fail('ChatResponse', 'expected object');
  const o = /** @type {any} */ (x);
  if (!Array.isArray(o.messages) || o.messages.length === 0) {
    fail('ChatResponse', 'messages must be a non-empty array');
  }
  for (const m of o.messages) {
    if (m === null || typeof m !== 'object') fail('ChatResponse', 'message must be an object');
    if (typeof m.text !== 'string') fail('ChatResponse', 'message.text must be a string');
    if (
      m.thinkingMs !== undefined &&
      (typeof m.thinkingMs !== 'number' || !Number.isFinite(m.thinkingMs) || m.thinkingMs < 0)
    ) {
      fail('ChatResponse', 'message.thinkingMs must be a non-negative number when present');
    }
    if (
      m.typingMs !== undefined &&
      (typeof m.typingMs !== 'number' || !Number.isFinite(m.typingMs) || m.typingMs < 0)
    ) {
      fail('ChatResponse', 'message.typingMs must be a non-negative number when present');
    }
  }
}

/** @param {unknown} x */
export function assertMessageFindingsResponse(x) {
  if (x === null || typeof x !== 'object' || !Array.isArray(x.findingsByMessage)) {
    fail('MessageFindingsResponse', 'findingsByMessage must be an array');
  }
  for (const row of x.findingsByMessage) {
    if (!row || !isNonEmptyString(row.messageId) || !Array.isArray(row.messageInferences)) {
      fail('MessageFindingsResponse', 'row shape invalid');
    }
    row.messageInferences.forEach(assertInference);
  }
}
